/**
 * Delete Distributor Form Composer
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  DeleteDistributorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type DeleteDistributorFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderTextControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type DeleteDistributorComposerProps = {
  distributorNumber: number
  distributorName: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof DeleteDistributorFormData, value: unknown): string | null => {
  const shape = DeleteDistributorFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

export function DeleteDistributorComposer(props: DeleteDistributorComposerProps) {
  const { distributorNumber, distributorName, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const deleteDistributor = useApiMutation('archiveDeviceDistributor', 'DELETE', {
    invalidates: ['getDeviceDistributors'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to archive distributor:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: DeleteDistributorFormSchema,
    defaultValues: {
      distributorNumber: distributorNumber,
      distributorName: distributorName,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as DeleteDistributorFormData
      const parsed = DeleteDistributorFormSchema.safeParse({
        distributorNumber: distributorNumber,
        distributorName: formValue.distributorName ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string' && (fieldName === 'distributorNumber' || fieldName === 'distributorName')) {
            form.setFieldMeta(fieldName as keyof DeleteDistributorFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      const apiRequest = transformToApiRequest(parsed.data)
      await deleteDistributor.mutateAsync(apiRequest)
    },
  })

  const renderControl = (control: any) => {
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof DeleteDistributorFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: false,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? true,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null
      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}
        {renderFormContent()}
        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: deleteDistributor.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Deleting...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Delete'),
            variant: 'destructive',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

export {
  DeleteDistributorFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type DeleteDistributorFormData,
} from './schema'
